<template>
  <!-- Accept modal — during forming, then held a beat once everyone is in (access-gated) -->
  <!-- Slots let the v5 shell show its own presentation over the same state, sounds and routing -->
  <slot v-if="showModal" name="found" :lobby="everyoneIn || lobby">
    <LobbyFoundModal :lobby="everyoneIn || lobby" />
  </slot>
  <!-- Party invite modal — one at a time, next shows once handled -->
  <slot v-else-if="canUseMatchmaking && pendingInvite" name="invite" :invite="pendingInvite">
    <PartyInviteModal :key="pendingInvite.id" :invite="pendingInvite" />
  </slot>
  <slot v-else-if="canUseMatchmaking" name="offer"><SeatOfferModal /></slot>
</template>

<script setup>
import {
  computed, ref, watch, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import LobbyFoundModal from './LobbyFoundModal.vue';
import PartyInviteModal from './PartyInviteModal.vue';
import SeatOfferModal from '../LanParty/SeatOfferModal.vue';
import { playSound, stopSound, preloadSounds } from '../../utils/matchmakingSounds';
import useAudioSettings from '../../composables/useAudioSettings';

// The v5 shell sends players to its own match room once there is one
const props = defineProps({
  lobbyRouteName: { type: String, default: 'MatchmakingLobbyPage' },
});

const store = useStore();
const router = useRouter();
const auth = useAuth();
// Keeps the sounds matchmaking plays in step with the player's Settings > Audio.
useAudioSettings();

const canUseMatchmaking = computed(() => !!auth.user()?.can_use_matchmaking);

const lobby = computed(() => store.getters.matchmakingLobby);
const pendingInvite = computed(() => store.getters.matchmakingPartyInvites[0] || null);

const EVERYONE_IN_MS = 1200;
const everyoneIn = ref(null);
let everyoneInTimer;

const forming = computed(() => canUseMatchmaking.value && lobby.value?.status === 'forming');
const showModal = computed(() => forming.value || !!everyoneIn.value);

const queued = computed(() => canUseMatchmaking.value && store.getters.matchmakingParty?.status === 'queued');

watch(canUseMatchmaking, (can) => {
  if (can) preloadSounds();
}, { immediate: true });

watch(queued, (isQueued) => {
  if (isQueued) {
    playSound('queue');
    playSound('searching');
    preloadSounds(['match-found', 'countdown', 'accepted']);
  } else {
    stopSound('searching', 0.15);
  }
});

// Already queued on mount, as after a reload mid-search: the search goes on, so its loop is asked
// for (and plays, or starts on an unmute), but the queue sound is not played again.
if (queued.value) {
  playSound('searching');
  preloadSounds(['match-found', 'countdown', 'accepted']);
}

watch(forming, (shown) => {
  if (shown) {
    stopSound('queue', 0.15);
    stopSound('searching', 0.15);
    playSound('match-found');
    playSound('countdown');
  } else {
    stopSound('match-found', 0.15);
    stopSound('countdown', 0.15);
  }
});

// Auto-navigate to lobby page once past forming
watch(lobby, (updated, previous) => {
  if (!canUseMatchmaking.value) return;
  if (previous?.status === 'forming' && updated && updated.status !== 'forming' && updated.status !== 'cancelled') {
    everyoneIn.value = updated;
    everyoneInTimer = setTimeout(() => {
      everyoneIn.value = null;
      router.push({ name: props.lobbyRouteName, params: { id: updated.uuid } });
    }, EVERYONE_IN_MS);
  }
});

onUnmounted(() => clearTimeout(everyoneInTimer));
</script>
